import Hotjar from '@hotjar/browser';
import { PlanNameEnum } from 'base/accounts_service';
import { EditorMode } from 'blocks/editor/editor_mode';
import posthog from 'posthog-js';
import { getApiHost } from './api_host';

// There's no validation on the backend.  Add new events here, please consider:
//
//   - Keep event names consistent.
//   - Don't add confusingly similar event names.
//
type AnalyticsEvent =
  | {
      event: 'blocks/click-export';
      destination:
        | 'figma'
        | 'webflow'
        | 'shareLink'
        | 'txt'
        | 'csv'
        | 'pricingCsv'
        | 'react'
        | 'html';
      method: 'header' | 'copy' | 'download';
      subject?: 'site' | 'page' | 'section';
      componentSlugs: string[];
      origin: ActionOrigin;
    }
  | {
      event: 'project:figma_install_plugin_button_click';
      method: 'modal' | 'figma_library';
    }
  | {
      event: 'project:figma_open_figma_kit_button_click';
      method: 'modal' | 'figma_library';
    }
  | {
      event: 'project:webflow_clone_style_guide_button_click';
      method: 'modal' | 'webflow_powerups';
    }
  | {
      event: 'project:webflow_install_app_button_click';
      method: 'modal' | 'webflow_powerups';
    }
  | {
      event: 'blocks/copy';
      object: 'page' | 'section';
      numPages: number;
      numSections: number;
      isCut: boolean;
      format: 'react' | 'html' | 'webflow' | 'figma';
      origin: ActionOrigin;
    }
  | {
      event: 'blocks/download';
      subject: 'site' | 'page' | 'section';
      format: 'react' | 'html';
      origin: ActionOrigin;
    }
  | ElementEvent
  | {
      event: 'blocks/generate-all-empty-pages-click';
      // Not present in the old event data:
      numPages: number;
    }
  | {
      event: 'blocks/change-mode';
      mode: 'sitemap' | 'wireframe' | 'styleguide' | 'design';
    }
  | {
      event: 'blocks/change-breakpoint';
      breakpoint: 'mobile' | 'tablet' | 'desktop';
    }
  | {
      event: 'blocks/add-space';
      method: 'blank';
    }
  | {
      event: 'blocks/wireframe-page-shuffle';
    }
  | AddPageEvent
  | AddSectionEvent
  | AddPanelImpressionEvent
  | {
      event: 'blocks/reset';
      object: 'copy' | 'elements' | 'all';
      subject: 'page' | 'section' | 'element' | 'node';
      componentSlugs: string[];
    }
  | {
      event: 'figma-ext/import-sitemap';
      blocksProjectId?: number;
      type: 'space' | 'page' | 'section';
      method: 'projects-panel';
      name: string;
    }
  | {
      event: 'figma-ext/import-wireframe';
      blocksProjectId?: number;
      type: 'element' | 'space' | 'page' | 'section';
      method: 'paste' | 'projects-panel';
      name?: string;
      componentSlugs: string[];
    }
  | {
      event: 'figma-ext:import_figma_kit_link_click';
      method: 'projects-panel';
    }
  | {
      event: 'webflow-ext/import-wireframe';
      blocksProjectId?: number;
      type: 'element' | 'space' | 'page' | 'section';
      method: 'paste' | 'projects-panel';
      name?: string;
      componentSlugs: string[];
    }
  | {
      event: 'react/click-view-code';
      data: {
        componentSlug: string;
      };
    }
  | {
      event: 'react/click-copy';
      data: {
        componentSlug: string;
        codeType: 'react' | 'html';
        page: 'list' | 'component';
      };
    }
  | {
      event: 'react/copy-event';
      data: {
        componentSlug: string;
        codeType: 'react' | 'html';
        page: 'list' | 'component';
        content: string;
      };
    }
  | {
      event: 'webflow/copy-event';
      componentSlug: string;
      style?: string;
      spacing?: string;
    }
  | {
      event: 'visit_font_information_button_clicked';
      link: string;
    }
  | CouponBannerImpressionEvent
  | CouponBannerClickEvent
  | GhostPageConversionEvent
  | WebsiteEvent
  | SignupFlowEvent
  | ReferralFlowEvent
  | UpgradeFlowEvent
  | ManageReferralAccountEvent
  | DashboardEvent
  | ImportSitemapEvent
  | ProjectEvent
  | SearchEvent
  | StyleGuideBuilderEvent
  | WebflowImportEvent
  | FigmaImportEvent;

type WebflowImportEvent =
  | WebflowImportProjectClickEvent
  | WebflowImportConceptStepContinueButtonClickEvent
  | WebflowImportPagesStepContinueButtonClickEvent
  | WebflowImportConflictStepContinueButtonClickEvent
  | WebflowImportDoneStepContinueButtonClickEvent
  | WebflowImportMissingFontSeenEvent
  | WebflowImportErrorEvent
  | WebflowImportDoneEvent;

type WebflowImportProjectClickEvent = {
  event: 'webflow_import:project_click';
  project_id: string;
};
type WebflowImportConceptStepContinueButtonClickEvent = {
  event: 'webflow_import:concepts_step_continue_button_click';
  project_id: string;
  concept_number?: number;
};
type WebflowImportPagesStepContinueButtonClickEvent = {
  event: 'webflow_import:pages_step_continue_button_click';
  project_id: string;
  concept_number?: number;
  pages_count: number;
};
type WebflowImportConflictStepContinueButtonClickEvent = {
  event: 'webflow_import:conflict_step_continue_button_click';
  project_id: string;
  concept_number?: number;
  pages_count: number;
  conflicts: 'replace' | 'create';
};
type WebflowImportDoneStepContinueButtonClickEvent = {
  event: 'webflow_import:done_step_continue_button_click';
  project_id: string;
  concept_number?: number;
  pages_count: number;
};
type WebflowImportMissingFontSeenEvent = {
  event: 'webflow_import:missing_font_seen';
  font_faces: string[];
};
type WebflowImportDoneEvent = {
  event: 'webflow_import:done';
  project_id: string;
  concept_number?: number;
  pages_count: number;
  duration: number;
};
type WebflowImportErrorEvent = {
  event: 'webflow_import:error';
  project_id: string;
  concept_number?: number;
  error: string;
};

type FigmaImportEvent =
  | FigmaImportProjectClickEvent
  | FigmaImportImportTypeClickEvent
  | FigmaImportMissingFontContinueEvent
  | FigmaImportStartEvent
  | FigmaImportDoneEvent
  | FigmaImportErrorEvent;

type FigmaImportProjectClickEvent = {
  event: 'figma_import:project_click';
  project_id: string;
};
type FigmaImportImportTypeClickEvent = {
  event: 'figma_import:import_type_click';
  project_id: string;
  // No event for 'sitemap' here because import begins immediately when it's
  // clicked, so it's already tracked by 'figma_import:start'.
  import_type: 'wireframe' | 'styleguide';
};
type FigmaImportStartEvent = {
  event: 'figma_import:start';
  project_id: string;
  concept_number?: number;
  pages_count?: number;
  import_type: 'sitemap' | 'wireframe' | 'styleguide';
};
type FigmaImportDoneEvent = {
  event: 'figma_import:done';
  project_id: string;
  concept_number?: number;
  pages_count?: number;
  duration: number;
  import_type: 'sitemap' | 'wireframe' | 'styleguide';
};
type FigmaImportErrorEvent = {
  event: 'figma_import:error';
  project_id: string;
  concept_number?: number;
  error: string;
  import_type: 'sitemap' | 'wireframe' | 'styleguide';
};
type FigmaImportMissingFontContinueEvent = {
  event: 'figma_import:missing_fonts_continue';
  missing_font_faces: string[];
  font_overrides: {
    original_name: string;
    override_family: string;
    override_style: string;
  }[];
};

type StyleGuideBuilderEvent =
  | ProjectEditColorButtonClickEvent
  | ProjectEditBrandButtonClickEvent
  | ProjectSurpriseMeButtonClickEvent
  | ProjectNewConceptButtonClickEvent
  | ProjectDuplicateConceptButtonClickEvent
  | ProjectDeleteConceptButtonClickEvent
  | ProjectViewConceptButtonClickEvent
  | ProjectTop3ConceptsButtonClickEvent
  | ProjectLightThemeButtonClickEvent
  | ProjectDarkThemeButtonClickEvent
  | ProjectShuffleColorButtonClickEvent
  | ProjectColorCardClickEvent
  | ProjectColorLockButtonClickEvent
  | ProjectColorDeleteButtonClickEvent
  | ProjectAddColorButtonClickEvent
  | ProjectUpdateNeutralTintButtonClickEvent
  | ProjectCopyColorButtonClickEvent
  | ProjectColorNameUpdateEvent
  | ProjectColorHexUpdateEvent
  | ProjectTypographySizeUpdateEvent
  | ProjectTypographyWeightUpdateEvent
  | ProjectTypographyStyleUpdateEvent
  | ProjectTypographyShuffleButtonClickEvent
  | ProjectTypographyCardClickEvent
  | ProjectTypographyViewButtonClickEvent
  | ProjectTypographyLockButtonClickEvent
  | ProjectLikeFontFamilyButtonClickEvent
  | ProjectUnlikeFontFamilyButtonClickEvent
  | ProjectSetFontFamilyButtonClickEvent
  | ProjectPreviewFontFamilyButtonHoverEvent
  | ProjectFontFamilySearchQueryUpdatedEvent
  | ProjectReloadPopularFontFamiliesButtonClickEvent
  | ProjectConceptLogoUpdateEvent
  | ProjectConceptLogoDeleteEvent
  | ProjectConceptLogoHeightUpdateEvent
  | ProjectImageShuffleEvent
  | ProjectSchemeShuffleEvent
  | ProjectConceptShortcutEvent
  | ProjectSectionSchemeUpdateEvent
  | ProjectImageUploadEvent
  | ProjectImageRemoveEvent;

type ProjectEditColorButtonClickEvent = {
  event: 'project:edit_color_button_click';
};
type ProjectEditBrandButtonClickEvent = {
  event: 'project:edit_brand_button_click';
};
type ProjectSurpriseMeButtonClickEvent = {
  event: 'project:surprise_me_button_click';
};
type ProjectNewConceptButtonClickEvent = {
  event: 'project:new_concept_button_click';
  concept_number: number;
};
type ProjectDuplicateConceptButtonClickEvent = {
  event: 'project:duplicate_concept_button_click';
  concept_number: number;
};
type ProjectDeleteConceptButtonClickEvent = {
  event: 'project:delete_concept_button_click';
  concept_number: number;
};
type ProjectViewConceptButtonClickEvent = {
  event: 'project:view_concept_button_click';
  concept_number: number;
};
type ProjectTop3ConceptsButtonClickEvent = {
  event: 'project:top_3_concepts_button_click';
};
type ProjectLightThemeButtonClickEvent = {
  event: 'project:light_theme_button_click';
};
type ProjectDarkThemeButtonClickEvent = {
  event: 'project:dark_theme_button_click';
};
type ProjectShuffleColorButtonClickEvent = {
  event: 'project:shuffle_color_button_click';
  location: 'color_editor' | 'concept_editor';
  palette?: 'chromatic';
  chromatic?: number;
};
type ProjectColorCardClickEvent = {
  event: 'project:color_card_click';
  palette: 'chromatic' | 'neutral';
  chromatic?: number;
};
type ProjectColorLockButtonClickEvent = {
  event: 'project:lock_color_button_click';
  palette: 'chromatic' | 'neutral';
  chromatic?: number;
};
type ProjectColorDeleteButtonClickEvent = {
  event: 'project:delete_color_button_click';
  palette: 'chromatic';
  chromatic: number;
};
type ProjectAddColorButtonClickEvent = {
  event: 'project:add_color_button_click';
  palette: 'chromatic';
  chromatic: number;
};
type ProjectUpdateNeutralTintButtonClickEvent = {
  event: 'project:update_neutral_tint_button_click';
  palette: 'neutral';
  chromatic: number | undefined;
};
type ProjectCopyColorButtonClickEvent = {
  event: 'project:copy_color_button_click';
  palette: 'chromatic' | 'neutral';
  chromatic?: number;
  shade?: number;
  hex: string;
  location: 'shades_view' | 'color_editor';
};
type ProjectColorNameUpdateEvent = {
  event: 'project:color_name_update';
  palette: 'chromatic';
  chromatic: number;
};
type ProjectColorHexUpdateEvent = {
  event: 'project:color_hex_update';
  palette: 'chromatic';
  chromatic: number;
  source: 'text' | 'visual';
};
type ProjectTypographySizeUpdateEvent = {
  event: 'project:typography_size_update';
  size: string;
};
type ProjectTypographyWeightUpdateEvent = {
  event: 'project:typography_weight_update';
  weight: string;
};
type ProjectTypographyStyleUpdateEvent = {
  event: 'project:typography_style_update';
  style: string;
};
type ProjectTypographyShuffleButtonClickEvent = {
  event: 'project:typography_shuffle_button_click';
  target?: 'heading' | 'body';
};
type ProjectTypographyCardClickEvent = {
  event: 'project:typography_card_click';
  target?: 'heading' | 'body';
};
type ProjectTypographyViewButtonClickEvent = {
  event: 'project:typography_view_button_click';
  target?: 'heading' | 'body';
};
type ProjectTypographyLockButtonClickEvent = {
  event: 'project:typography_lock_button_click';
  target?: 'heading' | 'body';
};
type ProjectLikeFontFamilyButtonClickEvent = {
  event: 'project:like_font_family_button_click';
  target: string;
  font_family: string;
  category: string;
  collection?: string;
};
type ProjectUnlikeFontFamilyButtonClickEvent = {
  event: 'project:unlike_font_family_button_click';
  target: string;
  font_family: string;
  category: string;
  collection?: string;
};
type ProjectSetFontFamilyButtonClickEvent = {
  event: 'project:set_font_family_button_click';
  target: string;
  font_family: string;
  category: string;
  collection?: string;
};
type ProjectPreviewFontFamilyButtonHoverEvent = {
  event: 'project:preview_font_family_button_hover';
  target: string;
  font_family: string;
  category: string;
  collection?: string;
};
type ProjectFontFamilySearchQueryUpdatedEvent = {
  event: 'project:font_family_search_query_updated';
  target: string;
  query: string;
};
type ProjectReloadPopularFontFamiliesButtonClickEvent = {
  event: 'project:reload_popular_font_families_button_click';
  target: string;
};
type ProjectConceptLogoUpdateEvent = {
  event: 'project:concept_logo_update';
  theme: 'light' | 'dark';
};
type ProjectConceptLogoDeleteEvent = {
  event: 'project:concept_logo_delete';
  theme: 'light' | 'dark';
};
type ProjectConceptLogoHeightUpdateEvent = {
  event: 'project:concept_logo_height_update';
  height: number;
};
type ProjectImageShuffleEvent = {
  event: 'project:image_shuffle';
  scope: 'section' | 'page';
  section_id?: string;
  section_slug?: string;
};
type ProjectSchemeShuffleEvent = {
  event: 'project:scheme_shuffle';
  scope: 'section' | 'page';
  section_id?: string;
  section_slug?: string;
  scheme_direction: 'shade' | 'palette';
};
type ProjectConceptShortcutEvent = {
  event:
    | 'project:page_shuffle_shortcut'
    | 'project:image_shuffle_shortcut'
    | 'project:typography_shuffle_shortcut'
    | 'project:full_width_preview_shortcut'
    | 'project:colors_shuffle_shortcut'
    | 'project:scheme_shuffle_button_click';
};
type ProjectSectionSchemeUpdateEvent = {
  event: 'project:section_scheme_update';
  section_id: string;
  section_slug: string;
  palette: 'chromatic' | 'neutral';
  chromatic?: number;
  shade: number;
};
type ProjectImageUploadEvent = {
  event: 'project:image_upload';
  section_id: string;
  section_slug?: string;
  index: number;
};
type ProjectImageRemoveEvent = {
  event: 'project:image_remove';
  section_id: string;
  section_slug?: string;
  index: number;
};

export type WebsiteEvent = WebsiteStartButtonClickEvent;
export type WebsiteStartButtonClickEvent = {
  event: 'website:start_button_click';
  button_location: 'navbar' | 'pricing_table';
  plan_name?: PlanNameEnum;
  billing_interval?: 'monthly' | 'yearly' | 'forever';
};

type SignupFlowEvent =
  | SignupFlowSurveySubmitEvent
  | SignupFlowSignupPageView
  | SignupFlowLoginButtonClick
  | SignupFlowWithGoogleSignUpSuccess
  | SignupFlowWithGoogleSignInSuccess
  | SignupFlowSignupFormSubmit
  | SignupFlowVerifyEmailSubmit
  | SignupFlowStartButtonClick
  | SignupFlowPaymentFormSubmit
  | SignupFlowTrialStarted
  | SignupFlowSubscriptionStarted;

type SignupFlowSurveySubmitEvent = {
  event: 'signup_flow:survey_submit';
  data?: Record<string, string>;
};
type SignupFlowSignupPageView = {
  event: 'signup_flow:signup_page_view';
  previous_path?: string;
};
type SignupFlowLoginButtonClick = { event: 'signup_flow:login_button_click' };
type SignupFlowWithGoogleSignUpSuccess = {
  event: 'signup_flow:with_google_sign_up_success';
};
type SignupFlowWithGoogleSignInSuccess = {
  event: 'signup_flow:with_google_sign_in_success';
};
type SignupFlowSignupFormSubmit = {
  event: 'signup_flow:signup_form_submit';
  referral_code?: string;
};
type SignupFlowVerifyEmailSubmit = { event: 'signup_flow:verify_email_submit' };
type SignupFlowStartButtonClick = {
  event: 'signup_flow:start_button_click';
  plan_name: PlanNameEnum;
  billing_interval?: 'monthly' | 'yearly' | 'forever';
};
type SignupFlowPaymentFormSubmit = {
  event: 'signup_flow:payment_form_submit';
  plan_name: PlanNameEnum;
  billing_interval?: 'monthly' | 'yearly' | 'forever';
  has_coupon: boolean;
};
type SignupFlowTrialStarted = {
  event: 'signup_flow:trial_started';
  plan_name: PlanNameEnum;
  billing_interval?: 'monthly' | 'yearly' | 'forever';
};
type SignupFlowSubscriptionStarted = {
  event: 'signup_flow:subscription_started';
  plan_name: PlanNameEnum;
  billing_interval?: 'monthly' | 'yearly' | 'forever';
};

type ReferralFlowEvent =
  | ReferralFlowDialogViewed
  | ReferralFlowInviteFriendsClick
  | ReferralFlowLinkViewed
  | ReferralFlowLinkCopied
  | ReferralFlowInviteBannerClicked
  | ReferralFlowInviteAndEarnButtonClicked
  | ReferralFlowInviteeBannerClicked
  | ReferralFlowInviteeDialogViewed;

type ReferralFlowDialogViewed = {
  event: 'referral_flow:dialog_viewed';
};
type ReferralFlowInviteFriendsClick = {
  event: 'referral_flow:invite_friends_click';
};
type ReferralFlowLinkViewed = {
  event: 'referral_flow:link_viewed';
  link: string;
};
type ReferralFlowLinkCopied = {
  event: 'referral_flow:link_copied';
  link: string;
};
type ReferralFlowInviteBannerClicked = {
  event: 'referral_flow:invite_banner_clicked';
};

type ReferralFlowInviteAndEarnButtonClicked = {
  event: 'referral_flow:invite_and_earn_button_clicked';
  location: 'dashboard' | 'sitebuilder';
};
type ReferralFlowInviteeBannerClicked = {
  event: 'referral_flow:invitee_banner_clicked';
};
type ReferralFlowInviteeDialogViewed = {
  event: 'referral_flow:invitee_dialog_viewed';
  referral_code: string;
  link: string;
};
type UpgradeFlowEvent =
  | UpgradeFlowUpgradePageView
  | UpdateFlowUpgradeButtonClick
  | UpgradeFlowPaymentFormSubmit
  | UpgradeFlowPaywallModalView
  | UpgradeFlowPaywallUpgradeButtonClick
  | UpgradeFlowPaywallComparePlansButtonClick
  | UpgradeFlowPaywallCloseButtonClick
  | UpgradeFlowTrialStarted
  | UpgradeFlowSubscriptionStarted;
type UpgradeFlowUpgradePageView = {
  event: 'upgrade_flow:upgrade_page_view';
  previous_path?: string;
};
type UpdateFlowUpgradeButtonClick = {
  event: 'upgrade_flow:upgrade_button_click';
  plan_name: PlanNameEnum;
  billing_interval?: 'monthly' | 'yearly' | 'forever';
};
type UpgradeFlowPaymentFormSubmit = {
  event: 'upgrade_flow:payment_form_submit';
  plan_name: PlanNameEnum;
  billing_interval?: 'monthly' | 'yearly' | 'forever';
  has_coupon: boolean;
};
type UpgradeFlowTrialStarted = {
  event: 'upgrade_flow:trial_started';
  plan_name: PlanNameEnum;
  billing_interval?: 'monthly' | 'yearly' | 'forever';
  referral_code?: string;
};
type UpgradeFlowSubscriptionStarted = {
  event: 'upgrade_flow:subscription_started';
  plan_name: PlanNameEnum;
  billing_interval?: 'monthly' | 'yearly' | 'forever';
  referral_code?: string;
};
type UpgradeFlowPaywallModalView = {
  event: 'upgrade_flow:paywall_modal_view';
  feature: string;
};
type UpgradeFlowPaywallUpgradeButtonClick = {
  event: 'upgrade_flow:paywall_upgrade_button_click';
  feature: string;
};
type UpgradeFlowPaywallComparePlansButtonClick = {
  event: 'upgrade_flow:paywall_compare_plans_button_click';
  feature: string;
};
type UpgradeFlowPaywallCloseButtonClick = {
  event: 'upgrade_flow:paywall_close_button_click';
  feature: string;
};

type ManageReferralAccountEvent = {
  event: 'manage-account:referral';
  previous_path?: string;
};

type DashboardEvent =
  | DashboardDashboardPageViewEvent
  | DashboardImportButtonClickEvent
  | DashboardProjectCreateEvent
  | DashboardProjectDuplicateEvent
  | DashboardProjectDeleteEvent;
type DashboardDashboardPageViewEvent = {
  event: 'dashboard:dashboard_page_view';
  tab: string | undefined;
  previous_path?: string;
};
type DashboardImportButtonClickEvent = {
  event: 'dashboard:import_button_click';
};
type DashboardProjectDeleteEvent = {
  event: 'dashboard:project_delete';
  project_id?: string;
};
type DashboardProjectDuplicateEvent = {
  event: 'dashboard:project_duplicate';
  project_id?: string;
};
type DashboardProjectCreateEvent = {
  event: 'dashboard:project_create';
  owner: 'team' | 'user';
};

type ImportSitemapEvent =
  | ImportSitemapDialogView
  | ImportSitemapStartButtonClickEvent
  | ImportSitemapCloseButtonClickEvent
  | ImportSitemapProjectCreateEvent;
type ImportSitemapCloseButtonClickEvent = {
  event: 'import_sitemap:close_button_click';
};
type ImportSitemapStartButtonClickEvent = {
  event: 'import_sitemap:start_button_click';
  import_domain: string;
};
type ImportSitemapProjectCreateEvent = {
  event: 'import_sitemap:project_create';
  import_domain: string;
};
type ImportSitemapDialogView = {
  event: 'import_sitemap:import_sitemap_dialog_view';
  behaviour: 'import-into-new-project' | 'import-into-existing-project';
};

type ProjectEvent =
  | ProjectCommentCreateEvent
  | ProjectCommentDeleteEvent
  | ProjectCommentResolveEvent
  | ProjectCommentUnresolveEvent
  | ProjectSitemapGenerateEvent
  | ProjectMoveButtonClickEvent
  | ProjectShareButtonClickEvent
  | ProjectAskAiButtonClickEvent
  | ProjectCopyShareLinkButtonClickEvent
  | ProjectCopyUpdateEvent
  | ProjectCopyGenerateEvent
  | ProjectComponentsShuffleEvent
  | ProjectCopyResetEvent
  | ProjectReplaceComponentButtonClickEvent
  | ProjectComponentSearchQueryUpdatedEvent;
type ProjectCommentCreateEvent = {
  event: 'project:comment_create';
  editor_mode: EditorMode;
  comment_type: 'toplevel' | 'reply';
  comment_viewport: string;
  comment_thread_id?: number;
};
type ProjectCommentDeleteEvent = {
  event: 'project:comment_delete';
  comment_id: number;
  comment_thread_id: number;
};
type ProjectCommentResolveEvent = {
  event: 'project:comment_resolve';
  comment_thread_id: number;
};
type ProjectCommentUnresolveEvent = {
  event: 'project:comment_unresolve';
  comment_thread_id: number;
};
type ProjectShareButtonClickEvent = {
  event: 'project:share_button_click';
  button_location: 'file_menu' | 'header';
};
type ProjectMoveButtonClickEvent = {
  event: 'project:move_button_click';
  button_location: 'dashboard' | 'share_dialog';
  move_to: 'user' | 'team';
};
type ProjectCopyShareLinkButtonClickEvent = {
  event: 'project:copy_share_link_button_click';
  share_link_access_level?: number;
  extra?: unknown;
};
type ProjectAskAiButtonClickEvent = {
  event: 'project:ask_ai_button_click';
  button_location: 'text_area';
  component_slug?: string;
};
type ProjectSitemapGenerateEvent = {
  event: 'project:sitemap_generate';
  generate_page_count: number;
  has_example_brief: boolean;
  has_generated_sitemap: boolean;
};
type ProjectCopyUpdateEvent = {
  event: 'project:copy_update';
  component_slug?: string;
};
type ProjectCopyGenerateEvent = {
  event: 'project:copy_generate';
  copy_generate_scope: 'page' | 'section';
  has_copy: boolean;
  component_slug?: string;
};
type ProjectComponentsShuffleEvent = {
  event: 'project:components_shuffle';
  components_shuffle_scope: 'page';
};
type ProjectCopyResetEvent = {
  event: 'project:copy_reset';
  copy_reset_scope: 'page' | 'section';
  component_slug?: string;
};
type ProjectReplaceComponentButtonClickEvent = {
  event: 'project:replace_component_button_click';
  component_slug?: string;
};
type ProjectComponentSearchQueryUpdatedEvent = {
  event: 'project:component_search_query_updated';
  query: string;
  component_type: string;
};

type SearchEvent =
  | SearchQueryUpdatedEvent
  | SearchComponentLinkClickedEvent
  | SearchComponentPreviewButtonClicked
  | SearchComponentCopyButtonClicked
  | SearchProjectLinkClicked;
type SearchQueryUpdatedEvent = {
  event: 'search:query_updated';
  query: string;
  mode: string;
};
export type SearchComponentEvents =
  | SearchComponentLinkClickedEvent
  | SearchComponentPreviewButtonClicked
  | SearchComponentCopyButtonClicked;
type SearchComponentLinkClickedEvent = {
  event: 'search:component_link_clicked';
  query: string;
  mode: string;
  component_slug: string;
  category_slug: string;
};
type SearchComponentPreviewButtonClicked = {
  event: 'search:component_preview_button_clicked';
  query: string;
  mode: string;
  component_slug: string;
  category_slug: string;
};
type SearchComponentCopyButtonClicked = {
  event: 'search:component_copy_button_clicked';
  query: string;
  mode: string;
  component_slug: string;
  category_slug: string;
};
type SearchProjectLinkClicked = {
  event: 'search:project_link_clicked';
  query: string;
  mode: string;
  project_id: string;
};

export type AddPageEvent = {
  event: 'blocks/add-page';
  method: 'result' | 'blank';
  name?: string;
  componentSlugs?: string[];
  direction?: 'before' | 'after' | 'below';
  search?: string;
  categoryName?: string;
};

export type AddSectionEvent = {
  event: 'blocks/add-section';
  method: 'result' | 'blank' | 'variant';
  name?: string;
  isGlobal?: boolean;
  previousComponentSlug?: string;
  componentSlug?: string;
  categoryName?: string;
  search?: string;
  interaction?: 'add' | 'replace';
  variantGroup?: string;
  variantGroupItem?: string;
  willRegenerateCopy?: boolean;
};

export type AddPanelImpressionEvent = {
  event: 'blocks/add-panel-impression';
  type: 'pages' | 'sections';
  search?: string;
  categoryName?: string;
};

export type CouponBannerImpressionEvent = {
  event: 'coupons/banner-impression';
  location: 'webflow-page' | 'projects-list' | 'manage-plan';
  couponCodes: string[];
};

export type CouponBannerClickEvent = {
  event: 'coupons/banner-click';
  location: CouponBannerImpressionEvent['location'];
  couponCode: string;
};

export type GhostPageConversionEvent = {
  event: 'blocks/ghost-page-conversion';
  name?: string;
  to: 'ghost' | 'page';
};

export enum ActionOrigin {
  ContextMenu = 'context-menu',
  Keyboard = 'keyboard',
  Button = 'button',
  DragAndDrop = 'drag-and-drop',
  Header = 'header',
}

type ElementEvent = (
  | { event: 'blocks/delete-failed'; error: string }
  | {
      event: 'blocks/delete-element';
    }
  | {
      event: 'blocks/add-element';
    }
  | {
      event: 'blocks/move-element';
    }
  | {
      event: 'blocks/duplicate-element';
    }
  | {
      event: 'blocks/paste-element';
    }
) & {
  origin: ActionOrigin;
  sectionRootSlug: string;
  slotName: string;
  elementSlug: string;
  elementRootName: string;
  elementCanonical: string;
};

// These aren't used anymore.  They're kept here for documentation.
type DeprecatedAnalyticsEvents = {
  event: 'blocks/click-find-component';
  name: string;
  // Should use "method" for future events
  trigger: 'menu' | 'sidebar';
};

export class AnalyticsService {
  private readonly getToken: () => PromiseLike<string> | string;
  private readonly getUserId?: () => string | null;
  private readonly getContext: () => object;
  private readonly captureException: (err: unknown) => void;

  constructor({
    getToken,
    getUserId,
    getContext = () => ({}),
    captureException,
  }: {
    readonly getToken: () => PromiseLike<string> | string;
    readonly getUserId?: () => string | null;
    readonly getContext?: () => object;
    readonly captureException: (err: unknown) => void;
  }) {
    this.getToken = getToken;
    this.getUserId = getUserId;
    this.getContext = getContext;
    this.captureException = captureException;
  }

  withContext(getMoreContext: () => object) {
    return new AnalyticsService({
      getToken: this.getToken,
      getUserId: this.getUserId,
      getContext: () => ({
        ...this.getContext(),
        ...getMoreContext(),
      }),
      captureException: this.captureException,
    });
  }

  track(event: AnalyticsEvent) {
    let { event: name, ...data } = event;
    data = { ...this.getContext(), ...data };
    console.groupCollapsed('[Relume Analytics]', name);
    console.table(data);
    console.groupEnd();
    void this.trackViaPosthog(name, data);
    void this.trackViaHotjar(name);
    void this.trackViaApi(name, data);
    void this.trackViaMetaPixel(event);
    void this.trackViaGtag(event);
  }

  private async trackViaApi(name: string, data: object) {
    try {
      const token = await this.getToken();
      const res = await window.fetch(`${getApiHost()}v1/ae`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name, data }),
        keepalive: true,
      });
      if (!res.ok) {
        throw new Error(`${res.status}: ${await res.text()}`);
      }
    } catch (err) {
      console.error('Failed to send analytics event internally', err);
      this.captureException(err);
    }
  }

  private trackViaPosthog(name: string, data: object) {
    posthog.capture(name, data, { send_instantly: true });
  }

  private trackViaHotjar(name: string) {
    try {
      if (!Hotjar.isReady()) return;
      Hotjar.event(name);
    } catch (err) {
      console.error('Failed to send analytics event via hotjar', err);
      this.captureException(err);
    }
  }

  private trackViaMetaPixel(event: AnalyticsEvent) {
    if (!window?.fbq) return;
    switch (event.event) {
      case 'blocks/change-mode':
        if (event.mode === 'wireframe') {
          window.fbq('trackCustom', 'WireframeGenerated', event, {
            deduplicate: true,
          });
        }
        break;
      case 'blocks/click-export':
      case 'blocks/copy':
      case 'project:move_button_click':
      case 'project:share_button_click':
        window.fbq('trackCustom', 'ActivationStarted', event, {
          deduplicate: true,
        });
        break;
      case 'webflow-ext/import-wireframe':
      case 'figma-ext/import-wireframe':
      case 'figma-ext/import-sitemap':
        window.fbq('trackCustom', 'ActivationCompleted', event, {
          deduplicate: true,
        });
        break;
    }
  }

  private trackViaGtag(event: AnalyticsEvent) {
    if (!window?.gtag) return;
    switch (event.event) {
      case 'signup_flow:signup_form_submit':
        window.gtag('event', 'conversion', {
          send_to: 'AW-428198720/m4MCCK_T0-wZEMCWl8wB',
          value: event,
        });
        break;
      case 'blocks/change-mode':
        if (event.mode === 'wireframe') {
          window.gtag('event', 'conversion', {
            send_to: 'AW-428198720/ZeetCJXs1-wZEMCWl8wB',
            value: event,
          });
        }
        break;
      case 'blocks/click-export':
      case 'blocks/copy':
      case 'project:move_button_click':
      case 'project:share_button_click':
        window.gtag('event', 'conversion', {
          send_to: 'AW-428198720/AZm2CJjs1-wZEMCWl8wB',
          value: event,
        });
        break;
      case 'webflow-ext/import-wireframe':
      case 'figma-ext/import-wireframe':
      case 'figma-ext/import-sitemap':
        window.gtag('event', 'conversion', {
          send_to: 'AW-428198720/YM0qCJvs1-wZEMCWl8wB',
          value: event,
        });
        break;
      case 'upgrade_flow:trial_started':
      case 'signup_flow:trial_started':
        window.gtag('event', 'conversion', {
          send_to: 'AW-428198720/0Ip2CMuE2OwZEMCWl8wB',
          value: event,
        });
        break;
      case 'upgrade_flow:subscription_started':
      case 'signup_flow:subscription_started':
        window.gtag('event', 'conversion', {
          send_to: 'AW-428198720/l8QMCLb11-wZEMCWl8wB',
          value: event,
        });
        break;
    }
  }
}
